import React, { useMemo, useState } from "react";

import { css } from "@compiled/react";
import {
    ComponentProps,
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";

import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useExperiment } from "@/utils/experiment";
import { isProduction } from "@/utils/urls";

const WRAPPER_STYLES = css({
    position: "relative"
});

const PANEL_STYLES = css({
    position: "absolute",
    insetBlockStart: token("space.050"),
    insetInlineEnd: token("space.050"),
    zIndex: 2147483001,
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "stretch",
    color: token("color.text.inverse"),
    border: 0,
    borderRadius: "8px",
    maxWidth: "260px",
    padding: `${token("space.050")} ${token("space.075")}`,
    backgroundColor: wacToken("color.extended.blue900"),
    backdropFilter: "blur(4px)",
    opacity: 0.76,
    transition:
        "background-color 220ms ease, backdrop-filter 220ms ease, opacity 220ms ease, padding 220ms ease"
});

const PANEL_EXPANDED_STYLES = css({
    backgroundColor: wacToken("color.extended.blue900"),
    backdropFilter: "blur(12px)",
    opacity: 1,
    padding: token("space.075")
});

// Inner row that keeps the panel name and the variant <select> side-by-side
// while the experiment-override toggle stacks beneath them.
const PANEL_ROW_STYLES = css({
    display: "inline-flex",
    alignItems: "center",
    columnGap: token("space.050"),
    minWidth: 0
});

const LABEL_STYLES = css({
    fontSize: "11px",
    lineHeight: 1.2,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    maxWidth: "140px"
});

const SELECT_STYLES = css({
    "border": 0,
    "borderRadius": "999px",
    "padding": `${token("space.025")} ${token("space.075")}`,
    "fontSize": "10px",
    "lineHeight": 1.2,
    "fontWeight": 700,
    "cursor": "pointer",
    "color": token("color.text"),
    "backgroundColor": token("elevation.surface"),
    ":focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

const EXPERIMENT_TOGGLE_ROW_STYLES = css({
    overflow: "hidden",
    maxHeight: 0,
    marginBlockStart: 0,
    opacity: 0,
    pointerEvents: "none",
    transform: "translateY(-4px)",
    transition:
        "max-height 220ms ease, margin-block-start 220ms ease, opacity 220ms ease, transform 220ms ease"
});

const EXPERIMENT_TOGGLE_ROW_EXPANDED_STYLES = css({
    maxHeight: "32px",
    marginBlockStart: token("space.100"),
    opacity: 1,
    pointerEvents: "auto",
    transform: "translateY(0)"
});

const EXPERIMENT_TOGGLE_LABEL_STYLES = css({
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    justifyContent: "space-between",
    columnGap: token("space.100"),
    fontSize: "11px",
    lineHeight: 1.2,
    fontWeight: 700,
    cursor: "pointer",
    userSelect: "none"
});

// Small native checkbox sized to fit alongside the existing variant <select>.
const EXPERIMENT_TOGGLE_CHECKBOX_STYLES = css({
    "width": "12px",
    "height": "12px",
    "margin": 0,
    "cursor": "pointer",
    ":disabled": {
        opacity: 0.4,
        cursor: "not-allowed"
    },
    ":focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

export interface UniformContextualBanditsContainerProps extends ComponentProps {
    name: string;
    useCaseId: string;
    experimentId?: string;
}

const OVERRIDE_DEFAULT = "default" as const;
type OverrideValue = typeof OVERRIDE_DEFAULT | number;

/**
 * Identity wrapper for `<UniformSlot>`. Renders whatever `items` array it is
 * given, in order. Used as the base wrapper when delegating selection to a
 * higher-level wrapper, and on the `default` slot.
 */
const VariantSelector = (props: UniformSlotWrapperComponentProps) => (
    <React.Fragment>
        {props.items.map((item, index) => (
            <React.Fragment key={`variant-${index}`}>{item}</React.Fragment>
        ))}
    </React.Fragment>
);

/**
 * Renders the `variants` slot but slices its `items` array down to a single
 * entry by index, so only the chosen variant is mounted.
 */
const renderChosenVariant = (chosenVariantIndex: number) => (
    <UniformSlot
        name="variants"
        wrapperComponent={(slotProps) =>
            VariantSelector({
                ...slotProps,
                items: [slotProps.items[chosenVariantIndex]]
            })
        }
    />
);

const useIsEnrolledInExperiment = ({
    experimentId = ""
}: {
    experimentId?: string;
}) => {
    const hasExperimentId = experimentId.length > 0;

    const experimentStatus = useExperiment({
        experimentName: experimentId,
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            // Do not fire exposure when there is no experiment to expose to.
            fireExperimentExposure: hasExperimentId,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            // Skip targeting evaluation entirely when no experiment is set.
            cohort: () => hasExperimentId
        },
        metadata: {
            description:
                "Contextual Bandits Experiment - configured in Uniform",
            expectedReleaseDate: "unknown - configured in Uniform",
            expectedRuntime: "unknown - configured in Uniform",
            context: {
                urls: []
            }
        }
    });

    return (
        hasExperimentId &&
        experimentStatus.type === "enrolled" &&
        experimentStatus.value
    );
};

const toggleExperimentOverride = (
    setExperimentOverride: React.Dispatch<React.SetStateAction<boolean>>
): void => {
    setExperimentOverride((prev) => !prev);
};

const UniformContextualBanditsContainer = (
    props: UniformContextualBanditsContainerProps
) => {
    const isProd = isProduction();

    // Stable reference to the `variants` slot entries. Required so authors can
    // see how many variant candidates exist and so downstream selection logic
    // does not re-run on every render.
    const variantSlots = useMemo(
        () => props.component.slots?.["variants"] ?? [],
        [props.component.slots]
    );

    // Dev-only override. In production this stays at `OVERRIDE_DEFAULT` and the
    // <select> is never rendered, so the value has no effect.
    const [override, setOverride] = useState<OverrideValue>(OVERRIDE_DEFAULT);

    const [isExperimentOverridden, setIsExperimentOverridden] =
        useState<boolean>(false);

    const [isPanelExpanded, setIsPanelExpanded] = useState<boolean>(false);

    // TODO(GWP-11355): replace with `useBanditDecision({ useCaseId, experimentId })`
    // returning the chosen variant index. Until the bandit decision hook lands,
    // always fall back to the `default` slot.
    const banditChosenIndex: number | null = null;

    const chosenVariantIndex: number | null =
        !isProd && override !== OVERRIDE_DEFAULT ? override : banditChosenIndex;

    const isEnrolledInExperiment = useIsEnrolledInExperiment({
        experimentId: props.experimentId
    });
    const variantChosen =
        chosenVariantIndex !== null && variantSlots[chosenVariantIndex];

    const isEnrolledInExperimentPlusOverride =
        !props.experimentId ||
        isEnrolledInExperiment ||
        (isExperimentOverridden && !isProd);

    const shouldRenderVariant =
        isEnrolledInExperimentPlusOverride && variantChosen;

    const renderedVariant = shouldRenderVariant ? (
        renderChosenVariant(chosenVariantIndex)
    ) : (
        <UniformSlot name="default" wrapperComponent={VariantSelector} />
    );

    if (isProd) {
        return renderedVariant;
    }

    return (
        <div css={WRAPPER_STYLES}>
            <div
                css={[PANEL_STYLES, isPanelExpanded && PANEL_EXPANDED_STYLES]}
                onMouseEnter={() => setIsPanelExpanded(true)}
                onMouseLeave={() => setIsPanelExpanded(false)}
                onFocus={() => setIsPanelExpanded(true)}
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setIsPanelExpanded(false);
                    }
                }}
            >
                <label css={PANEL_ROW_STYLES}>
                    <span css={LABEL_STYLES}>{props.name}</span>
                    <select
                        css={SELECT_STYLES}
                        aria-label={`Contextual bandits variant override for ${props.useCaseId}`}
                        value={String(override)}
                        onChange={(event) => {
                            const next = event.target.value;
                            setOverride(
                                next === OVERRIDE_DEFAULT
                                    ? OVERRIDE_DEFAULT
                                    : Number(next)
                            );
                        }}
                    >
                        <option value={OVERRIDE_DEFAULT}>default</option>
                        {variantSlots.map((_, index) => (
                            <option key={index} value={index}>
                                variant {index}
                            </option>
                        ))}
                    </select>
                </label>
                <div
                    css={[
                        EXPERIMENT_TOGGLE_ROW_STYLES,
                        isPanelExpanded && EXPERIMENT_TOGGLE_ROW_EXPANDED_STYLES
                    ]}
                    aria-hidden={!isPanelExpanded}
                >
                    <label
                        css={EXPERIMENT_TOGGLE_LABEL_STYLES}
                        title={
                            props.experimentId
                                ? `Force experiment "${props.experimentId}" to behave as enrolled in non-production environments. Does not affect real Statsig enrollment.`
                                : "No experimentId is configured on this contextual bandits container, so there is no experiment to override."
                        }
                    >
                        <span>Experiment Override</span>
                        <input
                            type="checkbox"
                            css={EXPERIMENT_TOGGLE_CHECKBOX_STYLES}
                            aria-label={`Force experiment enrollment override for ${props.useCaseId}`}
                            disabled={!props.experimentId}
                            checked={isExperimentOverridden}
                            // Removed from the tab order while the row is
                            // collapsed and visually hidden. Restored when the
                            // panel is hovered / focused.
                            tabIndex={isPanelExpanded ? 0 : -1}
                            onChange={() =>
                                toggleExperimentOverride(
                                    setIsExperimentOverridden
                                )
                            }
                        />
                    </label>
                </div>
            </div>
            {renderedVariant}
        </div>
    );
};

export default UniformContextualBanditsContainer;
